import { FC, useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useFormik } from 'formik';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import TextField from 'src/components/InputFields/TextField';
import ProductSelect from 'src/components/InputFields/ProductPicker';
import RawMaterialSelect from 'src/components/InputFields/RawMaterialPicker';
import { editProductionMapping } from 'src/services/productionMapping.service';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  errorMessages,
  productionMappingDialogSubtitles,
  productionMappingDialogTitles,
} from 'src/utils/properties.utils';
import { productionMappingValidation } from 'src/utils/validationScheme.utils';
import ProductionDialog from 'src/components/DialogBox/ProductionBox/ProductionDialog';
import { getBasicMaterials } from 'src/services/material.service';
import {
  AddItemIcon,
  AddItemText,
  ClickableContainer,
  RemoveItemIcon,
  OverflowContainer,
} from 'src/components/StyledComponents/StyledPageComponents';
import {
  FlexColumnBox,
  BorderedSectionBox,
  SectionTitleTypography,
  ItemRowBox,
  ItemFieldBox,
  ItemFieldAlignedBox,
  RemoveItemBox,
} from 'src/components/DialogBox/POBox/POBox.styles';
import { ProductionMapping } from 'src/models/Management.interface';

interface ProductionMappingEditDialogProps {
  open: boolean;
  mapping: ProductionMapping | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ProductionMappingEditDialog: FC<ProductionMappingEditDialogProps> = ({
  open,
  mapping,
  onClose,
  onSuccess,
}) => {
  interface RawMaterial {
    id: string;
    name: string;
    [key: string]: unknown;
  }

  const defaultProduction = {
    id: Date.now().toString(),
    rawMaterialId: '',
    quantity: '',
    detailId: undefined,
  };

  interface FormValues {
    productId: string;
    [key: string]: string;
  }

  const { showSnackbar } = StyledSnackbar();

  const [loading, setLoading] = useState(false);
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
  const [items, setItems] = useState<
    Array<{
      id: string;
      rawMaterialId: string;
      quantity: string;
      detailId?: string;
    }>
  >([]);

  const formik = useFormik<FormValues>({
    initialValues: {
      productId: '',
    },
    validationSchema: productionMappingValidation(items),
    onSubmit: (values) => handleSubmit(values),
  });

  useEffect(() => {
    loadRawMaterials();
  }, [open]);

  useEffect(() => {
    if (mapping && open) {
      const rawMaterials = mapping.details || mapping.rawMaterialDetails || [];

      const mappedItems = rawMaterials.map((detail, index) => ({
        id: `${Date.now()}-${index}`,
        rawMaterialId: detail.rawMaterialId,
        quantity: detail.quantity.toString(),
        detailId: detail.id,
      }));

      const itemsToSet =
        mappedItems.length > 0 ? mappedItems : [{ ...defaultProduction }];

      const initialValues: FormValues = {
        productId: mapping.productId,
      };

      itemsToSet.forEach((item) => {
        initialValues[`rawMaterialId-${item.id}`] = item.rawMaterialId;
        initialValues[`quantity-${item.id}`] = item.quantity;
      });

      formik.resetForm({ values: initialValues });
      setItems(itemsToSet);
    }
  }, [mapping, open]);

  const loadRawMaterials = async () => {
    if (open && rawMaterials.length === 0) {
      try {
        const materials = await getBasicMaterials();
        setRawMaterials(materials);
      } catch (error) {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);

    const details = items
      .map((item) => {
        const rawMaterialId = values[`rawMaterialId-${item.id}`];
        const quantity = values[`quantity-${item.id}`];

        return {
          ...(item.detailId && { id: item.detailId }),
          rawMaterialId,
          quantity: Number(quantity),
        };
      })
      .filter((item) => item.rawMaterialId && item.quantity > 0);

    if (details.length === 0) {
      showSnackbar(errorMessages.PLEASE_ADD_AT_LEAST_ONE_VALID_ITEM, 'error');
      setLoading(false);
      return;
    }

    try {
      const response = await editProductionMapping({
        id: mapping!.id,
        productId: values.productId,
        details,
      });
      showSnackbar(response.message, 'success');
      onSuccess();
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    formik.resetForm();
    setItems([{ ...defaultProduction }]);
    onClose();
  };

  const handleAddItem = () => {
    setItems([...items, { ...defaultProduction }]);
  };

  const handleRemoveItem = (id: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== id));
  };

  const selectedMaterialIds = items
    .map((item) => (formik.values as any)[`rawMaterialId-${item.id}`])
    .filter(Boolean);

  const calculateDialogHeight = () => {
    const baseHeight = 580;
    const itemHeight = 120;
    const additionalItems = items.length > 1 ? items.length - 1 : 0;
    return baseHeight + additionalItems * itemHeight;
  };

  return (
    <Box display="flex">
      <OverflowContainer display="flex" flexDirection="column">
        <ProductionDialog
          width="840px"
          height={`${calculateDialogHeight()}px`}
          open={open}
          handleClose={handleClose}
          title={productionMappingDialogTitles.EDIT}
          subtitle={productionMappingDialogSubtitles.EDIT}
          onSubmit={formik.handleSubmit}
          buttonDisabled={loading}
          buttonLoading={loading}
          buttonLabel="Update"
        >
          <Box display="flex" flexDirection="column">
            <FlexColumnBox>
              <ProductSelect
                color="var(--subtext)"
                width="630px"
                id="productId"
                label="Product"
                formikProps={formik}
                isUnmapped={true}
                additionalProducts={
                  mapping
                    ? [{ id: mapping.productId, name: mapping.productName }]
                    : []
                }
                disabledProductIds={mapping ? [mapping.productId] : []}
              />
            </FlexColumnBox>
            <BorderedSectionBox>
              <SectionTitleTypography>
                Raw Material Details
              </SectionTitleTypography>
              {items.map((item, index) => (
                <ItemRowBox key={item.id}>
                  <ItemFieldBox>
                    <RawMaterialSelect
                      showLabel={index === 0}
                      color="var(--subtext)"
                      width="300px"
                      id={`rawMaterialId-${item.id}`}
                      label="Raw Material"
                      formikProps={formik}
                      selectedIds={selectedMaterialIds}
                      rawMaterialsList={rawMaterials}
                    />
                  </ItemFieldBox>
                  <ItemFieldAlignedBox>
                    <TextField
                      showLabel={index === 0}
                      color="var(--subtext)"
                      width="300px"
                      id={`quantity-${item.id}`}
                      label="Quantity"
                      numberOnly
                      formikProps={formik}
                    />
                  </ItemFieldAlignedBox>
                  {items.length > 1 && (
                    <RemoveItemBox>
                      <RemoveItemIcon
                        component="i"
                        className="bx bx-minus-circle 1"
                        onClick={() => handleRemoveItem(item.id)}
                      />
                    </RemoveItemBox>
                  )}
                </ItemRowBox>
              ))}
            </BorderedSectionBox>
            <ClickableContainer
              width="130px"
              display="flex"
              mt={1}
              onClick={handleAddItem}
            >
              <AddItemIcon component="i" className="bx bx-plus" />
              <AddItemText>Add Item</AddItemText>
            </ClickableContainer>
          </Box>
        </ProductionDialog>
      </OverflowContainer>
    </Box>
  );
};

export default ProductionMappingEditDialog;
